import { useEffect, useState } from 'react';
import {
  CardAmountInfoSection,
  CardPaymentRow,
  CoCardsList,
  CoPromotionCard,
  LoanCards,
} from './EntryCOStyledComponents';

import {
  CardCheckbox,
  CardColumn,
  CardInput,
  CardLabel,
  CardRow,
  CardSection,
  CheckboxContainer,
  InfoCardMiniSpan,
  InitialAmount,
} from './../../components/CardInputs/CardsStyledComponents';
import { PaymentSelectText } from '../../components/CardInputs/CardsStyledComponents';
import {
  Label,
  TextBold,
  TextMedium,
} from '../../styles/GlobalStyledComponents';

import { theme } from '../../styles/Theme';
import { colors } from '../../styles/Colors';
import FinancialService from '../../services/Financial';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES_PATH } from '../../constants';
import {
  DebtResponse,
  CoLoanData,
  StandardPaymentType,
  StandardPaymentsData,
  TransactionReasonEnum,
  PromotionsData,
  PromotionTypeEnum,
} from '../../interfaces/Debt';
import { useDebtResult } from '../../contexts/DebtContext';
import { LoanResponse, PaymentCard } from '../../interfaces/Loan';
import { usePaymentSelected } from '../../contexts/PaymentSelectedContext';
import { useUserResult } from '../../contexts/UserContext';
import {
  ReusePaymentOptionalParams,
  ReusePaymentRequest,
} from '../../interfaces/Payment';
import { Transition } from '../Transition';
import TokenService from '../../services/Token';
import { useCardsResult } from '../../contexts/CardsContext';
import {
  calculateTotalDebtAmount,
  calculateTotalExcusedAmount,
  getDebtAmount,
  handleErrorPaymentClick,
  newPendingPayment,
  pricesHelper,
  removePaymentMethodError,
} from '../../utils/paymentsHelper';
import GAnalyticsService from '../../services/GAnalytics/ganalytics.service';
import dayjs from 'dayjs';
import { GTMFlowSteps, GTMPaymentPlanData } from '../../interfaces/GTM';
import { PaymentMethod } from '../../interfaces/PaymentMethod';
import { EntryContainer, PromotionsList } from '../Entry/EntryStyledComponents';
import CustomerHeader from '../../components/CustomerHeader/CustomerHeader';
import { ViewMode } from '../../interfaces/Mode';
import PaymentButtons from '../../components/EntryComponents/ContainerButtons/ContainerButtons';
import CustomPaymentCard from '../../components/EntryComponents/OptionsCards/AmountCard/OptionAmountCard';
import PlanPaymentCard from '../../components/EntryComponents/OptionsCards/PaymentCard/OptionPaymentCard';
import LoanCard from '../../components/EntryComponents/OptionsCards/LoanCard/LoanCard';
import { scrollToTop } from '../../utils/scrollHelper';
import { handleKeyDown, handleWheel } from '../../utils/formHelper';
import Promotions from '../../components/CardInputs/Promotions/Promotions';
import {
  PaymentPlanData,
  PaymentPlanParams,
  SimulationData,
} from '../../interfaces/PaymentPlan';
import { OriginalAmountBox } from '../../components/CardInputs/Promotions/PromotionsStyledComponents';
import { saveUrlParams } from '../../utils/urlParams';
import { initTracking } from '@pepper/utm-react';
import { convertLeadToken } from '../../utils/convertLeadToken';
import { InsightService } from '@pepper/insights-react';
import { StorageService } from '../../services/Storage';
import { useLedgerResult } from '../../contexts/LedgerContext';
import { setClarityUserId, setClaritySessionId } from '../../services/Clarity';

const EntryCO: React.FC = () => {
  const search = useLocation().search;
  const urlParams = new URLSearchParams(search);

  const leadToken = urlParams.get('lead_token');
  sessionStorage.setItem('advance-fee', 'false');
  sessionStorage.setItem('flow', 'co');
  localStorage.setItem('come-from-nodebt', 'false');
  const viewMode =
    urlParams.get('viewMode') || sessionStorage.getItem('pepper-view-mode');
  const pendingPayment =
    JSON.parse(StorageService.getPendingPayment() ?? '{}') || null;
  const { white, red4, red7 } = colors;
  const { textBold } = theme.fontWeight;
  const [cardList, setCardList] = useState<PaymentCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isSelectedAmount, setIsSelectedAmount] = useState<boolean>(false);
  const [isSelectedPaymentPlan, setIsSelectedPaymentPlan] =
    useState<boolean>(false);
  const [isSelectedPromotion, setIsSelectedPromotion] =
    useState<boolean>(false);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlanData[]>();
  const [quantity, setQuantity] = useState('');
  const [totalDebtAmount, setTotalDebtAmount] = useState<number>(0);
  const [maxDebtAmount, setMaxDebtAmount] = useState<number>(0);
  const [amountNotValid, setAmountNotValid] = useState<boolean>(false);
  const [initTime, setInitTime] = useState<dayjs.Dayjs>(dayjs());
  const [loansArray, setLoansArray] = useState<CoLoanData[]>([]);
  const [coLoanSelected, setCoLoanSelected] = useState<CoLoanData | null>(null);
  const [simulationData, setSimulationData] = useState<SimulationData[] | null>(
    null,
  );
  const [promotion, setPromotion] = useState<PromotionsData | null>(null);
  const [monthDay, setMonthDay] = useState<number>(0);
  const [amountSelected, setAmountSelected] = useState<number>(0);
  const [termSelected, setTermSelected] = useState<number>(0);
  const isCo = true;

  const navigate = useNavigate();

  const { debtResult, createDebtResult, deleteDebtResult } = useDebtResult();
  const {
    paymentSelected,
    createPaymentSelected,
    createCoPaymentSelected,
    deletePaymentSelected,
    deleteCoPaymentSelected,
  } = usePaymentSelected();
  const { userResult, createUserResult, deleteUserResult } = useUserResult();
  const { cardsResult, createCardsResult, deleteCardsResult } =
    useCardsResult();
  const { createLedgerResult } = useLedgerResult();

  const getCoLoanData = (loan: CoLoanData) => {
    if (loan) {
      const promotionData = getPromotion(loan.loan_id);
      // En el caso de que tenga promoción se cogerá su importe y no el total de la operación:
      const amount = promotionData?.amount || loan.certified_debt || 0;
      const transactionReason =
        loan.payment_plan && loan.payment_plan?.length > 0
          ? TransactionReasonEnum.PPG
          : TransactionReasonEnum.PCO;
      setCoLoanSelected({
        loan_id: loan.loan_id,
        certified_debt: amount,
        ag_code: loan.ag_code,
        collecting_account: loan.collecting_account,
        loan_reference: loan.loan_reference,
        next_due_fee: loan.next_due_fee || 0,
        product_type: loan.product_type,
        installments: loan.installments,
        allow_creation_payment_plan: loan.allow_creation_payment_plan,
        payment_plan: loan.payment_plan,
        transaction_reason:
          promotionData?.transaction_reason ?? transactionReason,
        type: promotionData?.type,
      });
      createCoPaymentSelected(loan);
      setIsSelectedPromotion(true);
      if (loan.payment_plan && loan.payment_plan.length > 0) {
        setPaymentPlan(loan.payment_plan);
      }
      GAnalyticsService.sendSelectOperation(loan.loan_id);
    }
  };

  const getUserData = async (documentId: string) => {
    try {
      const ledgersFound = await FinancialService.searchLedgers({
        document_id: documentId,
        has_available_amount: true,
      });
      const ledgerInfo = ledgersFound.ledgers.find(
        (ledger) => ledger.loan_id === pendingPayment?.loan_id,
      );
      const expirationDate = new Date(pendingPayment?.expiration_date);
      const now = new Date();
      if (
        ledgersFound.ledgers.length > 0 &&
        ledgerInfo &&
        ledgerInfo.available_amount > 0 &&
        expirationDate > now
      ) {
        createLedgerResult(ledgersFound);
        navigate(ROUTES_PATH.PAYMENT_ON_COURSE);
      } else {
        if (pendingPayment && ledgerInfo && expirationDate < now) {
          StorageService.removePendingPayment();
          createLedgerResult({ ledgers: [] });
        }
        let debtInfo: DebtResponse;
        if (debtResult && !leadToken) {
          debtInfo = debtResult;
          GAnalyticsService.saveDebtDataLayer(debtInfo, isCo);
        } else {
          debtInfo = await FinancialService.getDebtData(documentId);
          createDebtResult(debtInfo);
          GAnalyticsService.saveDebtDataLayer(debtInfo, isCo);
          setTotalDebtAmount(getDebtAmount(debtInfo) || 0);
        }

        if (debtInfo && debtInfo.loans.length > 0) {
          const loans = debtInfo.loans.filter(
            (loan) => loan.certified_debt! > 0,
          );
          setLoansArray(loans);
        }
        if (documentId) {
          getPaymentCards(documentId);
        }
      }
    } catch (error) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };
  const getPaymentCards = async (documentId: string) => {
    if (cardsResult) {
      setCardList(cardsResult.payment_methods);
    } else {
      try {
        const response = await FinancialService.getPaymentCards(documentId);
        createCardsResult(response);
        setCardList(response.payment_methods);
        GAnalyticsService.savePaymentMethodsDataLayer(response);
      } catch (error: any) {
        if (error?.result?.error) {
          navigate(ROUTES_PATH.TIMEOUT);
        } else {
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
        }
      }
    }
  };

  const getUserInfo = async () => {
    try {
      const response = await FinancialService.getUserData();
      createUserResult(response);
      await getUserData(response.document_id);
      GAnalyticsService.saveCustomerDataLayer(response, cardList);
      if (response.person_id) {
        GAnalyticsService.saveUserId(response.person_id);
        setClarityUserId(response.person_id);
        setClaritySessionId(response.person_id);
      }
    } catch (error: any) {
      if (error?.response?.status) {
        navigate(ROUTES_PATH.TIMEOUT);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const getPromotion = (loanId: string): PromotionsData | undefined => {
    if (debtResult) {
      const promotionData = debtResult.promotions.find(
        (e) => e.loan_id === loanId,
      );
      setPromotion(promotionData || null);
      return promotionData;
    }
  };

  const resetToEntryCO = () => {
    setCoLoanSelected(null);
    deleteCoPaymentSelected();
    setSimulationData(null);
    setIsSelected(true);
    setIsSelectedAmount(false);
    setIsSelectedPaymentPlan(false);
    setIsSelectedPromotion(false);
    navigate(ROUTES_PATH.ENTRY_CO);
    if (debtResult) {
      const promotions = debtResult.promotions.length > 0;
      const amount = getDebtAmount(debtResult, undefined, promotions);
      setTotalDebtAmount(amount ?? 0);
    }
    GAnalyticsService.sendBackStart();
  };

  const handlePaymentClick = async () => {
    if (userResult?.document_id && paymentSelected) {
      if (
        paymentSelected.transaction_reason === TransactionReasonEnum.POP ||
        paymentSelected.transaction_reason === TransactionReasonEnum.DEC ||
        paymentSelected.transaction_reason === TransactionReasonEnum.DEP ||
        paymentSelected.transaction_reason === TransactionReasonEnum.PAN
      ) {
        sessionStorage.setItem('able-to-advance-fee', 'false');
      } else {
        sessionStorage.setItem('able-to-advance-fee', 'true');
      }

      try {
        setLoading(true);
        if (isSelectedPaymentPlan) {
          await createPaymentPlanMethod(true);
          setInitTime(dayjs());
        } else {
          GAnalyticsService.sendFastPay(
            initTime,
            isSelectedAmount
              ? PromotionTypeEnum.DEBT_AMORTIZATION
              : promotion?.type || paymentSelected.type,
          );
          setInitTime(dayjs());
        }
        const paymentReuseData: ReusePaymentRequest = {
          document_id: userResult.document_id,
          loan_id: paymentSelected?.loan_id,
          payment_method_id: cardList[0].payment_method_id,
          amount: paymentSelected?.amount,
          transaction_reason: paymentSelected?.transaction_reason,
          access_mode: 'FAST_PAY',
        };
        const response = await FinancialService.reusePayment(paymentReuseData);
        const optionalParams: ReusePaymentOptionalParams = {
          transaction_reason: paymentSelected?.transaction_reason,
          excused_amount:
            promotion &&
            paymentReuseData.transaction_reason !== TransactionReasonEnum.DEC &&
            paymentReuseData.transaction_reason !== TransactionReasonEnum.CPP
              ? promotion.amount_excused
              : 0,
        };
        GAnalyticsService.saveReusePaymentDataLayer(response, optionalParams);
        GAnalyticsService.savePaymentCardType(cardList[0].type);
        if (cardsResult && cardsResult.payment_methods.length > 0) {
          GAnalyticsService.savePaymentMethodsDataLayer(
            cardsResult,
            PaymentMethod.EXPRESS,
          );
        }
        if (response.status === 'OK') {
          GAnalyticsService.sendContinuePayment(
            initTime,
            GTMFlowSteps.ENTRY_CO,
            {
              payment_method: PaymentMethod.EXPRESS,
              payment_transaction_reason: paymentReuseData.transaction_reason,
              payment_iin_number: cardList[0].iin_number,
              payment_card_brand: cardList[0].type,
              payment_third_used: '',
            },
          );
          GAnalyticsService.sendPurchase({
            currency: 'EUR',
            transaction_id: response.loan_id,
            value: response.amount,
            items: [
              {
                itemId: `payment-${response.payment_method_id}`,
                itemName: 'Payment-Landing',
              },
            ],
          });
          InsightService.logTrace('Payment Click Success', [
            { PaymentMethod: response.payment_method_id },
            { amount: response.amount.toString() },
            { loan_id: response.loan_id },
            { transaction_reason: paymentSelected.transaction_reason },
            { type: response.transaction_type },
            { route: ROUTES_PATH.ENTRY_CO },
          ]);
          navigate(ROUTES_PATH.SUCCESS);
          // Se guarda el pago realizado en el local storage
          newPendingPayment(response);
        } else {
          cardList.shift();
          setCardList(cardList);
          handleErrorPaymentClick(
            navigate,
            initTime,
            paymentSelected,
            GTMFlowSteps.ENTRY_CO,
            ROUTES_PATH.ENTRY_CO,
          );
        }
      } catch (error) {
        setCardList([]);
        handleErrorPaymentClick(
          navigate,
          initTime,
          paymentSelected,
          GTMFlowSteps.ENTRY_CO,
          ROUTES_PATH.ENTRY_CO,
        );
      } finally {
        setLoading(false);
      }
    } else {
      setCardList([]);
    }
  };

  const clearUser = () => {
    deleteDebtResult();
    deleteCardsResult();
    deletePaymentSelected();
    deleteCoPaymentSelected();
    deleteUserResult();
    TokenService.clearSession();
  };

  const onSelectFullDebt = () => {
    setIsSelected(true);
    setIsSelectedAmount(false);
    setIsSelectedPaymentPlan(false);
    setIsSelectedPromotion(false);
    if (debtResult) {
      let totalAmount = totalDebtAmount;
      if (debtResult.loans.length > 1) {
        totalAmount = calculateTotalDebtAmount(debtResult);
      } else if (debtResult.loans.length === 1) {
        totalAmount = debtResult.loans[0].certified_debt || 0;
        if (
          debtResult.loans[0].payment_plan &&
          debtResult.loans[0].payment_plan.length > 0
        ) {
          setPaymentPlan(debtResult.loans[0].payment_plan);
        }
      }
      let promotionData;
      if (coLoanSelected) {
        if (!promotion) {
          promotionData = getPromotion(coLoanSelected.loan_id);
          setPromotion(promotionData || null);
        }
        createCoPaymentSelected({
          ...coLoanSelected,
          loan_id: coLoanSelected?.loan_id,
          certified_debt: parseFloat(
            Number(coLoanSelected.certified_debt).toFixed(2),
          ),
          transaction_reason: coLoanSelected.transaction_reason,
          type: promotionData?.type ?? StandardPaymentType.PayOffDebt,
        });
      } else if (debtResult.loans.length > 0) {
        if (!promotion)
          promotionData = getPromotion(debtResult?.loans[0].loan_id);
        const promotionsExcusedAmount =
          debtResult?.promotions.length > 0
            ? calculateTotalExcusedAmount(debtResult)
            : 0;
        const fullPayment: StandardPaymentsData = {
          loan_id: debtResult?.loans[0]?.loan_id!,
          amount: parseFloat(
            Number(totalAmount - promotionsExcusedAmount).toFixed(2),
          ),
          amount_excused: promotionsExcusedAmount ?? 0,
          type: promotionData?.type ?? StandardPaymentType.PayOffDebt,
          transaction_reason:
            (paymentPlan && paymentPlan.length > 0) ||
            (debtResult?.loans[0]?.payment_plan &&
              debtResult?.loans[0]?.payment_plan.length > 0)
              ? TransactionReasonEnum.PPG
              : TransactionReasonEnum.PCO,
        };
        if (fullPayment.amount > 0) {
          createPaymentSelected(fullPayment);
        }
      }
    }
    setInitTime(dayjs());
  };

  const onSelectInputAmount = () => {
    setIsSelectedAmount(true);
    setIsSelected(false);
    setIsSelectedPaymentPlan(false);
    setIsSelectedPromotion(false);
    if (debtResult) {
      refreshMaxAmountInput(debtResult);
    }
  };

  const onSelectInputPlan = () => {
    simulatePaymentPlan();
    setIsSelectedPaymentPlan(true);
    setIsSelected(false);
    setIsSelectedAmount(false);
    setIsSelectedPromotion(false);
  };

  const onSelectPromotion = (value: PromotionsData) => {
    setIsSelectedPromotion(true);
    setIsSelected(false);
    setIsSelectedAmount(false);
    setIsSelectedPaymentPlan(false);
    if (
      loansArray.length > 1 &&
      value &&
      coLoanSelected &&
      coLoanSelected.loan_id === value.loan_id
    ) {
      createCoPaymentSelected({
        ...coLoanSelected,
        certified_debt: value.amount,
        transaction_reason: value.transaction_reason,
      });
    } else {
      const selectedPromotion = {
        loan_id: value.loan_id,
        amount: parseFloat(Number(value.amount).toFixed(2)),
        amount_excused: parseFloat(Number(value.amount_excused).toFixed(2)),
        type: value.type,
        transaction_reason: value.transaction_reason,
        category: value.category,
        expiration_date: value.expiration_date,
      };
      createPaymentSelected(selectedPromotion);
    }
  };

  const calculateNewSimulation = (value: number) => {
    if (value && value > 0) {
      const amount = simulationData?.find(
        (item) => item.term === parseFloat(Number(value).toFixed(2)),
      )?.installment;
      setAmountSelected(amount || 0);
      setTermSelected(value);
      if (loansArray.length === 1 && !coLoanSelected && paymentSelected) {
        const newPaymentPlan: StandardPaymentsData = {
          ...paymentSelected,
          amount: amount ?? 0,
          type: PromotionTypeEnum.PAYMENT_PLAN,
          transaction_reason: TransactionReasonEnum.CPP,
        };
        createPaymentSelected(newPaymentPlan);
      } else if (coLoanSelected) {
        const newPaymentPlanCo: CoLoanData = Object.assign({}, coLoanSelected, {
          certified_debt: amount,
          type: PromotionTypeEnum.PAYMENT_PLAN,
          transaction_reason: TransactionReasonEnum.CPP,
        });
        createCoPaymentSelected(newPaymentPlanCo);
      }
    }
  };

  const refreshMaxAmountInput = (debtInfo: DebtResponse) => {
    let amount = 0;
    if (coLoanSelected) {
      const promotion = debtInfo.promotions.find(
        (e) => e.loan_id === coLoanSelected.loan_id,
      );
      amount = getDebtAmount(debtInfo, coLoanSelected, !!promotion) || 0;

      if (promotion) {
        // ajustar el valor máximo sumando el amount y amount_excused si hay promo
        amount += promotion.amount_excused;
      }
      setMaxDebtAmount(Number(amount.toFixed(2)));
    } else if (paymentSelected && !coLoanSelected) {
      amount =
        getDebtAmount(debtInfo, undefined, debtInfo.promotions.length > 0) || 0;

      if (debtInfo.promotions.length > 0) {
        const promotionsExcusedAmount = calculateTotalExcusedAmount(debtInfo);
        amount += promotionsExcusedAmount;
      }
      setMaxDebtAmount(Number(amount.toFixed(2)));
    }
  };

  const handleAmountInput = (e: any) => {
    setQuantity(e.target.value);
    const processedValue = e.target.value.replace(/,/g, '.');
    amortizeAmount(processedValue);
  };

  const handleMonthDayInput = (day: number) => {
    setMonthDay(day);
  };

  const amortizeAmount = (processedValue: any) => {
    // Con está lógica se puede obtener el importe mínimo a 0 para las operaciones con plan de pago
    /* const minValue =
      (coLoanSelected && !coLoanSelected.allow_creation_payment_plan) ||
      (loansArray &&
        loansArray.length === 1 &&
        !loansArray[0].allow_creation_payment_plan)
        ? 0
        : debtResult?.min_payment_amount || 0; */
    const minValue = debtResult?.min_payment_amount || 0;
    if (
      processedValue > 0 &&
      processedValue >= minValue &&
      processedValue <= maxDebtAmount
    ) {
      setAmountNotValid(false);
      if (coLoanSelected) {
        const amortizedLoan: CoLoanData = Object.assign({}, coLoanSelected, {
          certified_debt: parseFloat(Number(processedValue).toFixed(2)),
          type: PromotionTypeEnum.DEBT_AMORTIZATION,
          transaction_reason: TransactionReasonEnum.DEC,
        });
        createCoPaymentSelected(amortizedLoan);
      } else if (loansArray && loansArray.length === 1) {
        const amortizedLoan: StandardPaymentsData = {
          loan_id: loansArray[0].loan_id,
          amount: parseFloat(Number(processedValue).toFixed(2)),
          amount_excused: 0,
          type: PromotionTypeEnum.DEBT_AMORTIZATION,
          transaction_reason: TransactionReasonEnum.DEC,
        };
        createPaymentSelected(amortizedLoan);
      }
    } else {
      setAmountNotValid(true);
      deletePaymentSelected();
    }
  };

  const simulatePaymentPlan = async () => {
    if ((coLoanSelected || paymentSelected) && simulationData === null) {
      const loanId = coLoanSelected?.loan_id || paymentSelected?.loan_id;
      try {
        // Se lanza la simulación:
        const simulation = await FinancialService.simulatePaymentPlan(loanId!);
        if (simulation) {
          setSimulationData(simulation.simulation_payments);
        }
      } catch (error: any) {
        if (error?.result?.error) {
          navigate(ROUTES_PATH.TIMEOUT);
        } else {
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
        }
      }
    }
  };

  const createPaymentPlanMethod = async (paymentOneClick?: boolean) => {
    let paymentPlanData: PaymentPlanParams | undefined;
    const loanSelected = coLoanSelected || paymentSelected;
    if (loanSelected) {
      // Se envía lo que haya ajustado el usuario en la simulación
      const paymentPlanSelected: PaymentPlanParams = {
        loan_id: loanSelected.loan_id, // coLoanSelected.loan_id,
        installment: amountSelected,
        term: termSelected,
        payment_day: monthDay,
      };
      try {
        const response =
          await FinancialService.createPaymentPlan(paymentPlanSelected);
        if (response) {
          // Se crea el plan de pagos
          paymentPlanData = paymentPlanSelected;
          sessionStorage.setItem('pepper-payment-plan', 'true');
          sessionStorage.setItem(
            'pepper-payment-plan-data',
            JSON.stringify(paymentPlanData),
          );
        }
      } catch (error: any) {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
        return;
      }
      if (!paymentOneClick) {
        const gtmPaymentPlanData: GTMPaymentPlanData = {
          term_selected: paymentPlanSelected.term,
          day_selected: paymentPlanSelected.payment_day,
          amount_selected: paymentPlanSelected.installment,
        };
        GAnalyticsService.sendContinueDebtType(
          initTime,
          PromotionTypeEnum.PAYMENT_PLAN,
          gtmPaymentPlanData,
        );
        navigate(ROUTES_PATH.TYPES_PAYMENTS);
      } else {
        const gtmPaymentPlanData: GTMPaymentPlanData = {
          term_selected: paymentPlanSelected.term,
          day_selected: paymentPlanSelected.payment_day,
          amount_selected: paymentPlanSelected.installment,
        };
        GAnalyticsService.sendFastPay(
          initTime,
          PromotionTypeEnum.PAYMENT_PLAN,
          gtmPaymentPlanData,
        );
      }
    }
  };

  const btnContinueDebtTypeCO = async () => {
    const loanSelected = coLoanSelected || paymentSelected;
    if (loanSelected) {
      if (isSelectedPaymentPlan) {
        createPaymentPlanMethod();
      } else {
        GAnalyticsService.sendContinueDebtType(
          initTime,
          isSelectedAmount
            ? PromotionTypeEnum.DEBT_AMORTIZATION
            : (promotion?.type ??
                loanSelected.type ??
                PromotionTypeEnum.AGED_TOTAL_DEBT_CHARGE_OFF),
        );
        navigate(ROUTES_PATH.TYPES_PAYMENTS);
      }
    }
  };

  const getLoanCO = async () => {
    try {
      const loans = await FinancialService.getLoans();
      const loanCo = loans.loans.find(
        (loan: LoanResponse) => loan.state === 'CO',
      );
      return loanCo?.state === 'CO';
    } catch (error: any) {
      return false;
    }
  };

  const clientRedirect = async () => {
    // Se comprueba si a pesar de no tener deuda CO es CO mediante la llamada a loans/searches
    const checkLoanCO = await getLoanCO();
    if (checkLoanCO) {
      navigate(ROUTES_PATH.CO_CLIENT_ERROR);
    } else {
      navigate(ROUTES_PATH.NO_DEBT);
    }
  };

  useEffect(() => {
    if (!debtResult) return;

    const hasNoDebt =
      debtResult.loans.length === 0 &&
      debtResult.standard_payments.length === 0 &&
      debtResult.promotions.length === 0;

    if (hasNoDebt) {
      // Si no hay deuda, se comprueba si es un cliente CO o no para redirigirle
      clientRedirect();
      return;
    }

    // Hay datos así que se quita el loading
    setLoading(false);

    if (!coLoanSelected && debtResult.loans.length > 0) {
      onSelectFullDebt(); // Se selecciona el pago completo por defecto
    }
  }, [debtResult]);

  useEffect(() => {
    if (debtResult) {
      if (coLoanSelected) {
        createCoPaymentSelected(coLoanSelected);
        setTotalDebtAmount(getDebtAmount(debtResult, coLoanSelected) || 0);
        refreshMaxAmountInput(debtResult);
        //setAmountNotValid(false);
        /* setQuantity(coLoanSelected.next_due_fee?.toString() || ''); */
        setQuantity('0,00');
        scrollToTop();
      } else if (!paymentSelected) {
        onSelectFullDebt();
      }
    }
  }, [coLoanSelected]);

  useEffect(() => {
    if (isSelectedAmount && quantity) {
      const processedValue = quantity.replace(/,/g, '.');
      amortizeAmount(processedValue);
    }
  }, [isSelectedAmount]);

  useEffect(() => {
    if (debtResult && !coLoanSelected) {
      setTotalDebtAmount(
        getDebtAmount(
          debtResult,
          undefined,
          debtResult.promotions.length > 0,
        ) || 0,
      );
      refreshMaxAmountInput(debtResult);
    }
  }, [paymentSelected]);

  useEffect(() => {
    if (!leadToken && userResult) {
      getUserData(userResult.document_id);
      GAnalyticsService.saveCustomerDataLayer(userResult, cardList);
    } else {
      clearUser();
      getUserInfo();
    }

    if (urlParams.size > 0 && process.env.REACT_APP_ENV && leadToken) {
      const trackedData = saveUrlParams(urlParams);
      if (trackedData) {
        const decodedToken = convertLeadToken(leadToken);
        initTracking(
          process.env.REACT_APP_ENV,
          decodedToken,
          trackedData.pclient_type || 'lead_token',
          'Landing_Pagos_CO',
        );
      }
    }

    if (sessionStorage.getItem('pepper-payment-method-error')) {
      removePaymentMethodError();
    }

    deletePaymentSelected();
    onSelectFullDebt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!loading && (
        <EntryContainer
          id="entry-page"
          $marginBottom={viewMode === ViewMode.IFRAME}
        >
          <CustomerHeader />
          <PaymentSelectText fontSize={16} lineHeight={22}>
            Selecciona qué quieres pagar:
          </PaymentSelectText>
          {debtResult && (
            <CoCardsList id="co-payments-list">
              {(loansArray.length === 1 ||
                (loansArray.length > 1 && !coLoanSelected) ||
                (coLoanSelected && !promotion)) && (
                <CardInput
                  onClick={() => {
                    if (!isSelected) {
                      onSelectFullDebt();
                      GAnalyticsService.sendChangeDebtType(
                        initTime,
                        (promotion?.type ?? paymentSelected?.type)!,
                      );
                      setInitTime(dayjs());
                    }
                  }}
                  selected={isSelected}
                  borderColor={red7}
                  cardPadding="24px"
                  id="co-payment-select-radio-btn"
                >
                  <CheckboxContainer id="co-payment-select-checkbox-container">
                    <CardCheckbox
                      id="co-payment-checkbox"
                      type="radio"
                      checked={isSelected}
                      readOnly
                    />
                    <Label
                      htmlFor="co-payment-checkbox"
                      id="co-payment-label"
                    ></Label>
                  </CheckboxContainer>
                  <CardColumn id="co-payment-column">
                    <CardRow id="co-payment-row">
                      <CardSection id="co-payment-section">
                        <TextBold fontSize={16} lineHeight={22}>
                          Pagar todo
                        </TextBold>
                      </CardSection>
                    </CardRow>
                    <CardSection id="co-payment-section-2">
                      <TextMedium fontSize={14} lineHeight={20} color={'gray6'}>
                        Paga todo en un único pago
                      </TextMedium>
                    </CardSection>
                    <CardAmountInfoSection id="co-payment-section-3">
                      <CardPaymentRow id="co-payment-row-2">
                        <CardSection id="co-payment-section-4">
                          {loansArray.length >= 1 &&
                            !coLoanSelected &&
                            calculateTotalDebtAmount(debtResult) > 0 && (
                              <OriginalAmountBox id="co-payment-initial-amount-box">
                                <InitialAmount
                                  fontSize={14}
                                  color={'red6'}
                                  id="co-payment-initial-amount-text"
                                >
                                  {pricesHelper(
                                    calculateTotalDebtAmount(debtResult),
                                  )}
                                  €
                                </InitialAmount>
                              </OriginalAmountBox>
                            )}
                          <TextBold fontSize={16} lineHeight={24}>
                            {pricesHelper(totalDebtAmount)} €
                          </TextBold>
                        </CardSection>
                        {loansArray.length >= 1 &&
                          !coLoanSelected &&
                          calculateTotalExcusedAmount(debtResult) > 0 && (
                            <CardSection id="co-savings-section">
                              <InfoCardMiniSpan id="co-savings-text">
                                <CardLabel fontSize={14} lineHeight={16}>
                                  Ahorras{' '}
                                  {pricesHelper(
                                    calculateTotalExcusedAmount(debtResult),
                                  )}
                                  €
                                </CardLabel>
                              </InfoCardMiniSpan>
                            </CardSection>
                          )}
                      </CardPaymentRow>
                    </CardAmountInfoSection>
                  </CardColumn>
                </CardInput>
              )}
              {loansArray.length > 1 && !coLoanSelected && (
                <LoanCards>
                  {loansArray.map((loan: CoLoanData, index) => (
                    <LoanCard
                      key={index}
                      loanId={loan.loan_id}
                      loanReference={loan.loan_reference}
                      debtAmount={loan.certified_debt || 0}
                      paymentPlan={
                        loan.payment_plan && loan.payment_plan.length > 0
                      }
                      onClick={() => getCoLoanData(loan)}
                    />
                  ))}
                </LoanCards>
              )}
              {(loansArray.length === 1 ||
                (loansArray.length > 1 && coLoanSelected)) && (
                <>
                  {debtResult && debtResult.promotions.length > 0 && (
                    <CoPromotionCard id="promotions-card">
                      <PromotionsList id="promotions-list">
                        {debtResult?.promotions.map(
                          (value: PromotionsData, index) => {
                            if (value.loan_id === coLoanSelected?.loan_id) {
                              return (
                                <Promotions
                                  key={index}
                                  promotion={value}
                                  onSelectInput={() => {
                                    if (!isSelectedPromotion) {
                                      onSelectPromotion(value);
                                      setIsSelectedPromotion(true);
                                      setInitTime(dayjs());
                                    }
                                  }}
                                  isSelected={isSelectedPromotion}
                                  loan={debtResult.loans.find(
                                    (e) => e.loan_id === value.loan_id,
                                  )}
                                />
                              );
                            }
                            return null;
                          },
                        )}
                      </PromotionsList>
                    </CoPromotionCard>
                  )}
                  {((loansArray.length === 1 &&
                    loansArray[0].allow_creation_payment_plan) ||
                    (loansArray.length > 1 &&
                      coLoanSelected &&
                      coLoanSelected.allow_creation_payment_plan)) && (
                    <PlanPaymentCard
                      isPaymentPlan={isSelectedPaymentPlan}
                      onSelectInputPlan={() => {
                        if (!isSelectedPaymentPlan) {
                          onSelectInputPlan();
                          GAnalyticsService.sendChangeDebtType(
                            initTime,
                            PromotionTypeEnum.PAYMENT_PLAN,
                          );
                          setInitTime(dayjs());
                        }
                      }}
                      totalDebtAmount={maxDebtAmount}
                      simulationData={simulationData || []}
                      handleMonthDay={handleMonthDayInput}
                      calculateNewSimulation={calculateNewSimulation}
                    />
                  )}
                  {((loansArray.length === 1 &&
                    loansArray[0].certified_debt! >
                      debtResult.min_payment_amount) ||
                    (loansArray.length > 1 &&
                      coLoanSelected &&
                      coLoanSelected.certified_debt! >
                        debtResult.min_payment_amount)) && (
                    <CustomPaymentCard
                      isSelectedAmount={isSelectedAmount}
                      onSelectInputAmount={() => {
                        if (!isSelectedAmount) {
                          onSelectInputAmount();
                          GAnalyticsService.sendChangeDebtType(
                            initTime,
                            PromotionTypeEnum.DEBT_AMORTIZATION,
                          );
                          setInitTime(dayjs());
                        }
                      }}
                      quantity={quantity}
                      handleAmountInput={handleAmountInput}
                      handleKeyDown={handleKeyDown}
                      handleOnWheel={handleWheel}
                      amountNotValid={amountNotValid}
                      debtResult={debtResult}
                      totalDebtAmount={maxDebtAmount}
                      lastPayment={0}
                      isPaymentPlan={
                        !!paymentPlan &&
                        !coLoanSelected?.allow_creation_payment_plan
                      }
                    />
                  )}
                </>
              )}
            </CoCardsList>
          )}
          <PaymentButtons
            cardList={cardList}
            white={white}
            red4={red4}
            textBold={textBold}
            paymentSelected={paymentSelected}
            isSelectedAmount={isSelectedAmount}
            quantity={quantity}
            amountNotValid={amountNotValid}
            coLoanSelected={coLoanSelected ? true : false}
            isCo={isCo}
            paymentClickButton={
              (cardList.length > 0 && !isCo) ||
              (cardList.length > 0 &&
                isCo &&
                paymentSelected &&
                loansArray.length === 1)
            }
            handlePaymentClick={handlePaymentClick}
            btnContinueDebtType={btnContinueDebtTypeCO}
            btnResetEntry={resetToEntryCO}
          />
        </EntryContainer>
      )}
      {loading && <Transition />}
    </>
  );
};

export default EntryCO;
